import { CarouselNoticebar, Drawer, Toast } from "@ad/anq-ui";
import React, { useEffect, useState } from "react";
import {
  getMonthList,
  getMonthPercentList,
  getSum,
  goalTypeList,
} from "../common";
import { GoalTypeEnums, QueryHomeInfoResult } from "@/api/home/interface";
import { multi2 } from "@/common/util/helpers";
import InputMoney, { formatNumber, getNumber } from "components/input_money";
import InputPercent from "components/input_percent";
import { updateGoal } from "@/api/home";

export interface GoalValueType {
  /** 字符串格式的值, 给后端的时候转一下 */
  perMonth: string[];
  yearTotal: string;
  perPercent?: number[];
}

interface Props {
  visible: boolean;
  onClose: () => void;
  homeInfo: QueryHomeInfoResult;
  onRefresh: () => void;
}

const INITIAL_GOAL_VALUE = {
  [GoalTypeEnums.GMV]: {
    perMonth: [],
    yearTotal: "",
    perPercent: [],
  },
  [GoalTypeEnums.COST]: {
    perMonth: [],
    yearTotal: "",
    perPercent: [],
  },
};

type ErrorType = Partial<
  Record<
    GoalTypeEnums,
    | {
        warn?: string;
        error?: string;
      }
    | undefined
  >
>;

const typeValueMap = {
  [GoalTypeEnums.GMV]: 1,
  [GoalTypeEnums.COST]: 2,
};

const DrawerGoalConfig = ({ visible, onClose, homeInfo, onRefresh }: Props) => {
  const [goalValue, setGoalVale] =
    useState<Record<GoalTypeEnums, GoalValueType>>();
  const [goalError, setGoalError] = useState<ErrorType>();

  useEffect(() => {
    if (visible) {
      initialInputFromHomeInfo();
    }
  }, [homeInfo, visible]);

  /** 从后端数据中初始化输入框 */
  const initialInputFromHomeInfo = () => {
    const yuanGmvPerMonth = homeInfo?.gmvGoalPerMonth?.map((item) =>
      multi2(item, 0.01)
    );
    const yuanCostPerMonth = homeInfo?.costGoalPerMonth?.map((item) =>
      multi2(item, 0.01)
    );
    const totalGmv = getSum(yuanGmvPerMonth);
    const totalCost = getSum(yuanCostPerMonth);

    setGoalVale(
      yuanGmvPerMonth && yuanCostPerMonth
        ? {
            [GoalTypeEnums.GMV]: {
              perMonth: yuanGmvPerMonth.map((item) => formatNumber(item)),
              yearTotal: formatNumber(totalGmv),
              perPercent: getMonthPercentList(yuanGmvPerMonth, totalGmv),
            },
            [GoalTypeEnums.COST]: {
              perMonth: yuanCostPerMonth.map((item) => formatNumber(item)),
              yearTotal: formatNumber(totalCost),
              perPercent: getMonthPercentList(yuanCostPerMonth, totalCost),
            },
          }
        : INITIAL_GOAL_VALUE
    );
  };

  const handleSubmit = async (type: GoalTypeEnums) => {
    const res = await updateGoal({
      type: typeValueMap[type],
      goalPerMonth: goalValue[type].perMonth
        .map((item) => getNumber(item))
        .map((item) => multi2(item, 100)),
    });

    return res?.success;
  };

  const handleClose = () => {
    onClose();
    setGoalVale(INITIAL_GOAL_VALUE);
    clearError();
  };

  const handleYearGoalChange = (v: string, type) => {
    clearError();

    // 年目标改变后, 季度目标=年目标*25%, 季度第一个月=年目标*8%, 季度第二个月=季度目标*8%, 季度第三个月=季度目标*9%
    const quarter1Month = multi2(getNumber(v), 0.08);
    const quarter2Month = multi2(getNumber(v), 0.08);
    const quarter3Month = multi2(getNumber(v), 0.09);
    const perMonthList = [
      quarter1Month,
      quarter2Month,
      quarter3Month,
      quarter1Month,
      quarter2Month,
      quarter3Month,
      quarter1Month,
      quarter2Month,
      quarter3Month,
      quarter1Month,
      quarter2Month,
      quarter3Month,
    ];

    setGoalVale((pre) => ({
      ...pre,
      [type]: {
        yearTotal: v,
        perMonth: getMonthList(perMonthList).map((item) => formatNumber(item)),
        perPercent: getMonthPercentList(perMonthList, getNumber(v)),
      },
    }));
  };

  const handleMonthValueChange = (v, type, index) => {
    clearError();
    const newMonthList = goalValue[type].perMonth.map((item, i) =>
      index === i ? v : item
    );

    setGoalVale((pre) => ({
      ...pre,
      [type]: {
        ...pre[type],
        perMonth: newMonthList,
        perPercent: getMonthPercentList(
          newMonthList.map((item) => getNumber(item)),
          getNumber(pre[type].yearTotal)
        ),
      },
    }));
  };

  const handleMonthPercentChange = (v: number, type, index) => {
    clearError();
    const newMonthList = goalValue[type].perMonth.map((item, i) =>
      index === i
        ? formatNumber(multi2(getNumber(goalValue[type].yearTotal), v / 100))
        : item
    );

    setGoalVale((pre) => ({
      ...pre,
      [type]: {
        ...pre[type],
        perMonth: newMonthList,
        perPercent: pre[type].perPercent.map((item, i) =>
          index === i ? v : item
        ),
      },
    }));
  };

  const clearError = () => {
    setGoalError(undefined);
  };

  const validateInput = () => {
    let newError: ErrorType;

    goalTypeList.forEach(({ type }) => {
      // 输入的每个月的总和
      const inputMonthTotal = getSum(
        goalValue[type].perMonth.map((item) => getNumber(item))
      );
      // 输入的年目标
      const inputYearTotal = getNumber(goalValue[type].yearTotal);
      const inputPercent = ((inputMonthTotal / inputYearTotal) * 100).toFixed(
        2
      );

      if (inputMonthTotal > inputYearTotal) {
        newError = newError || {};
        newError[type] = {
          error: `百分比总和为 ${inputPercent}% 超过 100%，月目标金额总和超过年目标金额，请调整`,
          warn: "",
        };
      } else if (inputMonthTotal < inputYearTotal) {
        newError = newError || {};
        newError[type] = {
          warn: `百分比总和为 ${inputPercent}% 不足 100%，月目标金额总和小于年目标金额`,
          error: "",
        };
      }
    });

    setGoalError(newError);

    return !newError;
  };

  const handleOk = async () => {
    if (
      validateInput() &&
      goalTypeList.every(({ type }) => goalValue[type]?.perMonth?.length)
    ) {
      let success = true;

      await Promise.all(
        goalTypeList.map(async ({ type }) => {
          const res = await handleSubmit(type);
          if (!res) {
            success = false;
          }
        })
      );

      if (success) {
        Toast.success("配置成功");
        onClose();
        onRefresh();
        setGoalVale(INITIAL_GOAL_VALUE);
      } else {
        Toast.error("配置失败");
      }
    }
  };

  return (
    <Drawer
      title="目标配置"
      width={800}
      onClose={handleClose}
      onOk={handleOk}
      visible={visible}
    >
      {goalTypeList.map((item) => (
        <div className="p-y-6 pt-0 text-text-black/[.95]" key={item.type}>
          <div className="mb-6 text-base font-semibold">
            {item.title}目标配置
          </div>
          <div className="mb-6 flex">
            <span className="mr-2 leading-8">年目标：</span>
            <InputMoney
              suffix="元"
              className="w-[320px]"
              value={goalValue?.[item.type].yearTotal}
              onChange={(v) => handleYearGoalChange(v, item.type)}
              placeholder="请输入"
            />
          </div>
          <div className="flex">
            <span className="mr-2 leading-8">月目标：</span>
            <div className="flex-1  rounded-lg bg-[#FAFAFC] p-6">
              {goalValue?.[item.type].yearTotal ? (
                <>
                  {goalError?.[item.type]?.error && (
                    <CarouselNoticebar
                      type="error"
                      content={goalError?.[item.type]?.error}
                      closable={false}
                      className="mb-3"
                    />
                  )}
                  {goalError?.[item.type]?.warn && (
                    <CarouselNoticebar
                      type="warn"
                      content={goalError?.[item.type]?.warn}
                      closable={false}
                      className="mb-3"
                    />
                  )}
                  <div className="grid grid-cols-2 gap-x-6 gap-y-3">
                    {new Array(12).fill(0).map((_, index) => (
                      <div key={String(index)} className="flex gap-1">
                        <span className="w-[32px] leading-[32px] text-text-black/[.65]">
                          {index + 1}月
                        </span>
                        <InputPercent
                          className="w-[80px]"
                          type="percent"
                          value={goalValue?.[item.type].perPercent[index]}
                          onChange={(v) =>
                            handleMonthPercentChange(v, item.type, index)
                          }
                          error={
                            goalValue?.[item.type].perPercent[index] > 100
                              ? "不能超过 100"
                              : ""
                          }
                        />
                        <InputMoney
                          className="flex-1"
                          suffix="元"
                          value={goalValue?.[item.type].perMonth[index]}
                          onChange={(v) =>
                            handleMonthValueChange(v, item.type, index)
                          }
                        />
                      </div>
                    ))}
                  </div>
                </>
              ) : (
                <div className="text-center text-sm text-text-black/[.65]">
                  请先设置年目标金额
                </div>
              )}
            </div>
          </div>
        </div>
      ))}
    </Drawer>
  );
};

export default DrawerGoalConfig;
